import React, { FunctionComponent, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import DateRangePicker from "../../../components/inputs/daterangepicker";
import { thunkFetchOnShelfAvailabilityStatistics } from "../../../store/statistics/onshelfavailability/actions";
import { AppState } from "../../../store";
import { OnShelfAvailabilityState } from "../../../store/statistics/onshelfavailability/types";
import { Grid } from "@material-ui/core";

import moment from "moment";
import { getDates } from "../shared/graphFunctions";
import TableWrapper from "../components/TableWrapper";
import LineChart from "../components/LineChart";
import { LineChartData } from "../components/LineChart/types";
import OutOfStockTable from "./OutOfStockTable";
import BarChart from "../components/BarChart";
import StatTable from "../components/StatTable";

export const OnShelfAvailabilityStatistics: FunctionComponent = () => {
  const dispatch = useDispatch();
  const tableRefs = new Map();

  const [daterange, setDateRange] = useState([
    moment().toDate(),
    moment().toDate()
  ]);

  const statisticsState = useSelector<AppState, OnShelfAvailabilityState>(
    s => s.onShelfAvailabilityStatistics
  );

  let { onShelfAvailabilityStatistics, isLoading } = statisticsState;

  useEffect(() => {
    if (daterange)
      dispatch(
        thunkFetchOnShelfAvailabilityStatistics(daterange[0], daterange[1])
      );
  }, [daterange]);

  const calculateAverage = (outOfStockProductsByDay: Map<string, number>) => {
    const data = Object.values(outOfStockProductsByDay).sort((a, b) => a - b);

    return data.reduce((a, b) => a + b) / data.length;
  };

  const populateChartData = (): LineChartData => {
    return {
      labels: getDates(daterange[0], daterange[1]).map(s =>
        moment(s).format("YYYY-MM-DD")
      ),
      datasets: onShelfAvailabilityStatistics.map(o => {
        return {
          label: o.storeName,
          data: Object.values(o.totalProductsOutOfStockDuringPeriod)
        };
      })
    };
  };

  return (
    <>
      <DateRangePicker
        disabled={isLoading}
        onChange={setDateRange}
        value={daterange}
      />
      <Grid
        container
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"flex-start"}
        style={{ marginTop: "2rem" }}
        spacing={3}
      >
        <Grid item xs={12} lg={7}>
          <TableWrapper title={"Products out of stock during period"}>
            <StatTable
              exportName={"Products out of stock"}
              isLoading={isLoading}
              columns={[
                { title: "Store", field: "storeName" },
                {
                  title: "Total nr. of products",
                  field: "totalProductsOutOfStock"
                },
                {
                  title: "Currently out of stock",
                  field: "currentProductsOutOfStock"
                },
                {
                  title: "Average during period",
                  field: "averageProductsOutOfStock"
                }
              ]}
              data={
                !isLoading
                  ? onShelfAvailabilityStatistics.map(m => {
                      tableRefs.set(m.storeName, React.createRef());
                      return {
                        storeName: m.storeName,
                        currentProductsOutOfStock:
                          m.totalProductsCurrentlyOutOfStock,
                        totalProductsOutOfStock: m.totalProductsOutOfStock,
                        outOfStockProducts: m.outOfStockProducts,
                        averageProductsOutOfStock: calculateAverage(
                          m.totalProductsOutOfStockDuringPeriod
                        ).toFixed(2)
                      };
                    })
                  : []
              }
              detailPanel={(rowData: any) => {
                return (
                  <OutOfStockTable
                    outOfStockProducts={rowData.outOfStockProducts}
                  />
                );
              }}
            />
          </TableWrapper>
        </Grid>
        <Grid item xs={12} lg={5}>
          <BarChart
            title={"Products currently out of stock"}
            chartData={{
              labels: onShelfAvailabilityStatistics.map(o => o.storeName),
              datasets: [
                {
                  label: "",
                  data: onShelfAvailabilityStatistics.map(
                    o => o.totalProductsCurrentlyOutOfStock
                  )
                }
              ]
            }}
            showLegend={false}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12}>
          <LineChart
            title={"Products out of stock during period"}
            isLoading={isLoading}
            chartData={populateChartData()}
          />
        </Grid>
      </Grid>
    </>
  );
};
